import { useEffect } from 'react';
import useApi from './useApi';

// Fetch all offers and set state in param (useCustomer.js style)
export default function useOffer(param) {
  const { get } = useApi();
  const offers = get('/offers');

  useEffect(() => {
    param.setLoading(offers.isLoading);
    if (!offers?.isLoading) {
      if (
        offers.data &&
        (offers.data.success === true || offers.data.status === 'success' || Array.isArray(offers.data))
      ) {
        // Set offers array in parent state
        param.setOffers(Array.isArray(offers.data) ? offers.data : offers.data.data);
      }
    }
  }, [offers.isLoading]);

  useEffect(() => {
    if (offers?.isError) {
      param.setError(offers?.error?.message || 'Failed to fetch offers');
    }
  }, [offers?.isError]);
}

// Fetch a single offer by ID and set in param
export function getOfferById(id, param) {
  const { getById } = useApi();
  const offer = getById('/offers', id);
  useEffect(() => {
    param.setLoading(offer.isLoading);
    if (!offer?.isLoading) {
      if (
        offer.data &&
        (offer.data.success === true || offer.data.status === 'success' || offer.data)
      ) {
        // Set single offer in parent state
        param.setOffer(offer.data.data || offer.data);
      }
    }
  }, [offer.isLoading]);
  useEffect(() => {
    if (offer?.isError) {
      param.setError(offer?.error?.message || 'Failed to fetch offer');
    }
  }, [offer?.isError]);
}

// Custom hook for adding an offer with side effects
export function useAddOffer(param = {}) {
  const { post } = useApi();
  return post('/offers', {
    onSuccess: (data) => {
      // Treat any successful response as success (backend may not include success/status flags)
      if (typeof param.setOffers === 'function') param.setOffers((prev) => [data, ...(prev || [])]);
      if (param.toast && typeof param.toast.success === 'function') param.toast.success('Offer added!');
    },
    onError: (error) => {
      // Show detailed backend error
      let message = 'Failed to add offer';
      if (error?.response?.data?.errors) {
        message = Object.entries(error.response.data.errors).map(([field, msgs]) => `${field}: ${msgs[0]}`).join('\n');
      } else if (error?.response?.data?.message) {
        message = error.response.data.message;
      } else if (error?.message) {
        message = error.message;
      }
      if (param.toast && typeof param.toast.error === 'function') param.toast.error(message);
    },
  });
}

// Update hooks disabled: editing offers has been removed intentionally

// Delete an offer and update state/toast
export function deleteOffer(id, param) {
  const { del } = useApi();
  const { mutate: remove, isLoading, isError } = del('/offers', {
    onSuccess: (data) => {
      if (data.data && (data.data.success === true || data.data.status === 'success')) {
        // Remove offer from offers state
        param.setOffers((prev) => prev.filter((o) => o.offer_id !== id));
        param.toast.success('Offer deleted!');
      } else {
        param.toast.error(data.message || 'Failed to delete offer');
      }
    },
    onError: (data) => {
      param.toast.error(data.message || 'Failed to delete offer');
    },
  });
  remove(id);
}

// Update offer status and update state/toast
export function useUpdateOffer(id, param = {}) {
  const { post } = useApi(); // ✅ IMPORTANT: post

  return post(`/offers/${id}`, {
    onSuccess: (data) => {
      if (param.toast?.success) param.toast.success("Offer updated!");
    },
    onError: (error) => {
      let message = "Failed to update offer";
      if (error?.response?.data?.errors) {
        message = Object.entries(error.response.data.errors)
          .map(([field, msgs]) => `${field}: ${msgs[0]}`)
          .join("\n");
      } else if (error?.response?.data?.message) {
        message = error.response.data.message;
      } else if (error?.message) {
        message = error.message;
      }
      if (param.toast?.error) param.toast.error(message);
    },
  });
}


export function useGetOffers() {
  const { get } = useApi();
  return get('/offers');
}
